import {
    LeafletPixiApiSettings,
    MapRenderer,
    MapRendererConfig,
    PixiEntitySelection,
    RendererName
} from "./OperationRenderer";
import * as PIXI from "pixi.js";
import { OverlaySetting } from "../LeafletPixiOverlayService";
import { UnitHashElement } from "../../../context/map/units/unitsStore";
import { getColorByStatusUnits } from "@utils/getColorByStatusUnit";
import Logger from "@utils/logger";
import mapStandArrowUnit from "@images/mapStandArrowUnit.svg";
import mapArrowUnit from "@images/mapArrowUnit.svg";
import { EntityId } from "@reduxjs/toolkit";
import { store } from "../../../store";
import { unitSelectors } from "../../../store/reducers/units";
import * as L from "leaflet";
import { Point } from "leaflet";

export type UnitGraphic = PIXI.Sprite & {}
const log = Logger.getLogger("unit.renderer")

export type UnitRendererEventNames = {
    'select': (selection: PixiEntitySelection) => void
}
export class UnitRenderer extends MapRenderer<UnitHashElement, EntityId[], UnitRendererEventNames> {
    enable() {
        super.enable();
        this.children.forEach(c => c.enable())
    }

    disable() {
        super.disable();
        this.children.forEach(c => c.disable())
    }

    children: MapRenderer<UnitHashElement>[] = []

    icons = {
        stand: PIXI.Texture.from(mapStandArrowUnit),
        move: PIXI.Texture.from(mapArrowUnit)
    }

    getZIndex(): number {
        return this.zIndex
    }

    name = RendererName.Unit as const
    enabled: boolean = false
    container: PIXI.Container | undefined = undefined
    selected?: UnitGraphic | undefined = undefined

    zIndex = 20
    opacity = .5

    constructor(config: MapRendererConfig, { children }: { children: MapRenderer<UnitHashElement>[] }) {
        super(config)
        this.children = children
    }

    onOutsideClick() {
        super.onOutsideClick();
        if (this.selected)
            this.selected.alpha = this.opacity
        this.service.refreshPixiContainer()
    }

    onPixiOverlayChange(settings: LeafletPixiApiSettings) {
        super.onPixiOverlayChange(settings);
        this.container?.children.forEach((graphic) => {
            const unitGraphic = graphic as UnitGraphic
            this.scaleUnit(unitGraphic, settings.scale || 1)
        })
    }

    scaleUnit(unitGraphic: UnitGraphic, scale: number) {
        let size = scale < 100 ? 20 / scale : 0.2
        unitGraphic.height = size
        unitGraphic.width = size
    }

    onUnitRightClick(graphic: UnitGraphic, unitId: EntityId) {
        return () => {
            const unit = unitSelectors.selectById(store.getState().units, unitId)
            if(unit) {
                log.debug("Right click on unit ", unit)
                // @ts-ignore
                const menu = this.map.contextmenu
                menu.removeAllItems()
                menu.addItem(
                    {
                        text: `Показать детали`,
                        callback: () => {
                            // log.debug('bounds', [unit.dynamic.position])
                            // const bound = new L.Bounds([unit.dynamic.position] as unknown as Point[])
                            // const { x, y } = bound.getCenter()
                            // this.map.setView([y, x], 15)
                            this.emit('select', { selectedUnits: [unitId] })
                        }
                    }
                )
            }
        }
    }

    onUnitClick(graphic: UnitGraphic, unit: UnitHashElement) {
        return () => {
            if (this.selected) {
                this.selected.alpha = this.opacity
            }
            this.selected = graphic
            this.selected.alpha = .7
            this.service.refreshPixiContainer()
        }
    }

    redraw(unitIds: EntityId[]): void {
        for (const unitId of unitIds) {
            const layerId = unitId as string
            const unit = unitSelectors.selectById(store.getState().units, unitId)
            const unitGraphic = this.container?.getChildByName(layerId) as UnitGraphic
            if (!unitGraphic) {
                log.warn("Not found unit graphic for id", layerId)
            } else if (unit) {
                let { move, stand } = this.icons
                let { project, scale } = this.settings
                // let { sizeScale } = this.service
                let { unitState, trackPoints, azimuth, position } = unit.dynamic

                unitGraphic.texture = trackPoints && trackPoints?.length > 1 ? move : stand
                unitGraphic.tint = Number(getColorByStatusUnits(unitState, 'DEC'))
                if (azimuth) unitGraphic.angle = azimuth
                if (position?.length) {
                    let pProject = project([position[1], position[0]])
                    unitGraphic.x = pProject.x
                    unitGraphic.y = pProject.y
                    unitGraphic.visible = true
                    if (scale) {
                        this.scaleUnit(unitGraphic, scale)
                    }
                }
                super.redraw([unitId])
            }
        }
    }

    render(unitIds: EntityId[]): void {
        for (const unitId of unitIds) {
            const unit = unitSelectors.selectById(store.getState().units, unitId)
            if (unit && !this.container?.getChildByName(unitId as string)) {
                let unitGraphic = new PIXI.Sprite() as UnitGraphic
                unitGraphic.name = unitId as string
                unitGraphic.interactive = true
                unitGraphic.buttonMode = true
                unitGraphic.alpha = this.opacity
                unitGraphic.zIndex = this.getZIndex() - 5
                unitGraphic.visible = false
                unitGraphic.anchor.set(0.5);

                unitGraphic.on('click', this.onUnitClick(unitGraphic, unit))
                unitGraphic.on('rightdown', this.onUnitRightClick(unitGraphic, unitId))

                this.container?.addChild(unitGraphic)
                super.render([unitId])
            }
        }
    }
}